import React, { useState, useCallback, useMemo, useContext } from "react";
import {
  BackLink,
  Title,
  Text,
  Field,
  TextField,
  Button,
  Autocomplete,
  DropdownPaginated,
  DateTimePickerPopup,
} from "components/commons";
import { prettifyProductCodeStyle } from "utils/pretty.utils";
import { ConfirmModal, SuccessModal } from "components/modals";
import { useModal, useRouter, useForm, useApi, useMount } from "hooks";
import locale from "localization";
import { Path, PriceBuildUpStatus } from "enums";
import classNames from "classnames";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import UserContext from "contexts/user.context";
import {
  getDepots,
  getStation,
  getStationByCode,
  createPriceBuildUp,
  getPriceBuildUps,
  getPriceBuildUp,
  getMovements,
} from "apis";
import styles from "./price-build-up-add.module.scss";
import Skeleton from "@material-ui/lab/Skeleton";

// ─── Constants ────────────────────────────────────────────────────────────────

const PRODUCTS = [
  { name: "Extreme 105", code: "gas105" },
  { name: "Extreme 97", code: "gas97" },
  { name: "Extreme 95", code: "gas95" },
  { name: "Extreme U", code: "gas91" },
  { name: "Diesel", code: "diesel" },
  { name: "Kerosene", code: "kerosene" },
  { name: "LPG", code: "lpg" },
];

const SUBSIDY_OPTIONS = [
  { label: "LBAM", value: "LBAM" },
  { label: "PBC", value: "PBC" },
  { label: "PAM", value: "PAM" },
];

// ─── Form initial state ───────────────────────────────────────────────────────

const initialFormState = () => ({
  stationCode: {
    value: "",
    required: true,
    validations: [
      ({ value }) => {
        if (value && value.length < 4) {
          return { error: true, message: "Station code must be 4 digits." };
        }
        return { error: false, message: "" };
      },
    ],
  },
  effectiveDateTime: {
    value: new Date(),
    required: true,
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseNum = (val) => parseFloat(val) || 0;
const fmt2 = (val) => parseNum(val).toFixed(2);
const fmt4 = (val) => parseNum(val).toFixed(4);

const isNumericPeriod = (val, decimals = 2) => {
  if (val === "" || val === undefined) return true;
  return new RegExp(`^[0-9]*\\.?[0-9]{0,${decimals}}$`).test(val);
};

const isNumericPeriodMinus = (val, decimals = 4) => {
  if (val === "" || val === undefined) return true;
  return new RegExp(`^-?[0-9]*\\.?[0-9]{0,${decimals}}$`).test(val);
};

const createInitialRow = (product) => ({
  productName: product.name,
  productCode: product.code,
  depot: "",
  depotLabel: "",
  freight: "",
  pumpPrice: "",
  previousPumpPrice: "",
  previousPumpPriceDisabled: false,
  previousFinalWpp: "",
  previousFinalWppDisabled: false,
  stationUm: "",
  wppAdjustment: "",
  wppAdjustmentDisabled: false,
});

const createInitialSubsidyAmounts = () =>
  PRODUCTS.reduce((acc, p) => ({ ...acc, [p.code]: "" }), {});

const computeWpp = (row, subsidyRowsArg) => {
  const totalSubs = subsidyRowsArg.reduce(
    (sum, sr) => sum + parseNum(sr.amounts[row.productCode] || 0),
    0,
  );
  return parseNum(row.pumpPrice) - parseNum(row.freight) - totalSubs - parseNum(row.stationUm);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StationDetails = ({ details }) => (
  <div className={styles.stationDetails}>
    <div className={styles.stationDetailsGrid}>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.stationName}</Text>
        <Text className={styles.detailValue}>{details.name}</Text>
      </div>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.customerName}</Text>
        <Text className={styles.detailValue}>{details.businessName}</Text>
      </div>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.stationType}</Text>
        <Text className={styles.detailValue}>{details.stationType}</Text>
      </div>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.island}</Text>
        <Text className={styles.detailValue}>{details.island?.toUpperCase()}</Text>
      </div>
      <div className={styles.stationDetailItem}>
        <Text className={styles.detailLabel}>{locale.area}</Text>
        <Text className={styles.detailValue}>{details.area}</Text>
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const PriceBuildUpAddModule = () => {
  const { history } = useRouter();
  const { user } = useContext(UserContext);
  const confirmModal = useModal();
  const successModal = useModal();

  const formInitialState = useMemo(() => initialFormState(), []);
  const { fields, modifyField, isFormSubmittable } = useForm({
    initialState: formInitialState,
  });

  const stationCodeValue = fields.stationCode.value;

  // ── State ──
  const [stationDetails, setStationDetails] = useState(null);
  const [stationCodeError, setStationCodeError] = useState("");
  const [productRows, setProductRows] = useState(PRODUCTS.map(createInitialRow));
  // subsidyRows: [{ name: "LBAM", amounts: { gas91: "", gas95: "", ... } }]
  const [subsidyRows, setSubsidyRows] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [conflictStatus, setConflictStatus] = useState(null);
  const [movementConflict, setMovementConflict] = useState(false);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);
  const [allDepotsData, setAllDepotsData] = useState([]);
  const [enabledProducts, setEnabledProducts] = useState([]);

  // ── APIs ──
  const { request: fetchAllDepots, loading: isLoadingAllDepots } = useApi({
    api: getDepots,
    handleOwnError: true,
    params: {
      page: 1,
      perPage: 9999,
      orderBy: "name",
    },
  });

  const {
    request: fetchStation,
    loading: loadingStation,
    result: stationResult,
  } = useApi({
    api: getStationByCode,
    handleOwnError: true,
  });

  const { request: fetchStationById } = useApi({
    api: getStation,
    handleOwnError: true,
  });

  const { request: fetchRecentBuiltUps, loading: isLoadingGetPriceBuildup } = useApi({
    api: getPriceBuildUps,
    handleOwnError: true,
  });

  const { request: fetchPriceBuildUp, loading: isLoadingGetPriceBuildups } = useApi({
    api: getPriceBuildUp,
    handleOwnError: true,
  });

  const { request: submitCreate } = useApi({
    api: createPriceBuildUp,
    handleOwnError: true,
  });

  const { request: checkConflict } = useApi({
    api: getPriceBuildUps,
    handleOwnError: true,
  });

  const { request: checkMovementConflict } = useApi({
    api: getMovements,
    handleOwnError: true,
  });

  useMount(async () => {
    const res = await fetchAllDepots({ page: 1, perPage: 9999 });
    setAllDepotsData(res?.depots || []);
  });

  // ── Depot paginated request ──
  const depotRequest = useCallback(
    async ({ searchKey }) => {
      const filtered = searchKey
        ? allDepotsData.filter((d) => d.depotCode.toLowerCase().includes(searchKey.toLowerCase()))
        : allDepotsData;
      return { depots: filtered, count: filtered.length };
    },
    [allDepotsData],
  );

  // ── Auto-populate from most recent price build-up ──
  const populateFromRecentBuildUp = useCallback(
    async (stationCode, enabledProducts, effectiveDateTime) => {
      try {
        const listRes = await fetchRecentBuiltUps({
          page: 1,
          perPage: 1,
          searchKey: stationCode,
          searchType: "stationCode",
          statuses: [PriceBuildUpStatus.Active, PriceBuildUpStatus.Expired, PriceBuildUpStatus.Scheduled].join(","),
          orderBy: "effectiveAt",
          order: "desc",
          endDate: moment(effectiveDateTime).toISOString(),
        });
        const items = listRes?.priceBuildUps || [];
        if (!items.length) return;

        const priceBuildupId = items[0]?.priceBuildupId;
        if (!priceBuildupId) return;
        const detail = await fetchPriceBuildUp({ priceBuildUpId: priceBuildupId });
        if (!detail) return;

        const details = detail?.priceBuildupDetails || [];

        setProductRows(
          enabledProducts.map((product) => {
            const d = details.find((item) => item.productCode === product.code);
            if (!d)
              return {
                ...createInitialRow(product),
                previousPumpPriceDisabled: true,
                previousFinalWppDisabled: true,
              };
            return {
              productName: product.name,
              productCode: product.code,
              depot: d.depotId ? String(d.depotId) : "",
              depotLabel:
                allDepotsData.find((depot) => depot.depotId === d.depotId)?.depotCode || "",
              freight: d.freight != null ? fmt4(d.freight) : "",
              pumpPrice: d.pumpPrice != null ? fmt2(d.pumpPrice) : "",
              previousPumpPrice: d.pumpPrice != null ? fmt2(d.pumpPrice) : "",
              previousPumpPriceDisabled: !!details,
              previousFinalWpp: d.finalWpp != null ? fmt4(d.finalWpp) : "",
              previousFinalWppDisabled: !!details,
              stationUm: d.stationUm != null ? fmt2(d.stationUm) : "",
              wppAdjustment: d.wppAdjustment != null ? fmt4(d.wppAdjustment) : "",
              wppAdjustmentDisabled: false,
            };
          }),
        );

        const newSubsidyRows = [];
        const subsidyKeys = ["LBAM", "PBC", "PAM"];
        const fieldMap = { LBAM: "lbam", PBC: "pbc", PAM: "pam" };

        subsidyKeys.forEach((key) => {
          const hasAnyValue = details.some(
            (d) => d[fieldMap[key]] != null && d[fieldMap[key]] !== 0,
          );
          if (!hasAnyValue) return;
          const amounts = enabledProducts.reduce((acc, product) => {
            const d = details.find((item) => item.productCode === product.code);
            const val = d ? d[fieldMap[key]] : null;
            acc[product.code] = val != null ? fmt2(val) : "";
            return acc;
          }, {});
          newSubsidyRows.push({ name: key, amounts });
        });

        setSubsidyRows(newSubsidyRows);
      } catch {
        // silently ignore — form is still usable without pre-population
      }
    },
    [fetchRecentBuiltUps, fetchPriceBuildUp, allDepotsData],
  );

  // ── Station code lookup ──
  const handleStationCodeChange = useCallback(
    async (_, { value }) => {
      const numeric = value.replace(/\D/g, "").slice(0, 4);
      modifyField("stationCode", { value: numeric });
      setStationDetails(null);
      setStationCodeError("");
      setProductRows([]);
      setSubsidyRows([]);
      setConflictStatus(null);

      if (numeric.length === 4) {
        try {
          const res = await fetchStation({ stationCode: numeric });
          setStationDetails(res);
          const stationData = await fetchStationById({ stationId: res.stationId });
          const stationProduct = stationData?.stationProduct || {};
          const enabledProductsList = PRODUCTS.filter((p) => stationProduct[p.code] === true);
          setProductRows(enabledProductsList.map(createInitialRow));
          setEnabledProducts(enabledProductsList);
          populateFromRecentBuildUp(numeric, enabledProductsList, fields.effectiveDateTime.value);
        } catch {
          setStationCodeError(locale.stationCodeNotFound);
          setStationDetails(null);
        }
      }
    },
    [
      modifyField,
      fetchStation,
      fetchStationById,
      populateFromRecentBuildUp,
      fields.effectiveDateTime.value,
    ],
  );

  const handleEffectiveDateTimeChange = useCallback(
    async (name, { value }) => {
      modifyField(name, { value });
      setConflictStatus(null);
      setMovementConflict(false);

      if (value && stationDetails) {
        const startEffectiveAt = moment(value).startOf("minute").toISOString();
        const endEffectiveAt = moment(value).endOf("minute").toISOString();
        setIsCheckingConflict(true);
        try {
          const res = await checkConflict({
            page: 1,
            perPage: 1,
            searchKey: fields.stationCode.value,
            searchType: "stationCode",
            statuses: [
              PriceBuildUpStatus.Active,
              PriceBuildUpStatus.Scheduled,
              PriceBuildUpStatus.Expired,
            ].join(","),
            startDate: startEffectiveAt,
            endDate: endEffectiveAt,
          });
          if (res?.priceBuildUps?.length > 0) {
            const rawStatus = res.priceBuildUps[0]?.status || "";
            setConflictStatus(rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase());
          }
          if (moment(value).isAfter(moment())) {
            const mvRes = await checkMovementConflict({
              page: 1,
              perPage: 1,
              dateRangeBy: "effectiveAt",
              startDate: startEffectiveAt,
              endDate: endEffectiveAt,
            });
            if (mvRes?.movements?.length > 0) {
              setMovementConflict(true);
            }
          }
        } catch {
        } finally {
          setIsCheckingConflict(false);
        }

        if (enabledProducts.length) {
          populateFromRecentBuildUp(fields.stationCode.value, enabledProducts, value);
        }
      }
    },
    [
      modifyField,
      stationDetails,
      enabledProducts,
      fields.stationCode.value,
      populateFromRecentBuildUp,
      checkConflict,
      checkMovementConflict,
    ],
  );

  // ── Row updater ──
  const updateRow = useCallback((index, patch) => {
    setProductRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }, []);

  // ── Depot change ──
  const handleDepotChange = useCallback(
    (index, { value, label }) => {
      updateRow(index, {
        depot: value || "",
        depotLabel: label || "",
        freight: "",
      });
    },
    [updateRow],
  );

  // ── Subsidy helpers ──
  const handleAddSubsidyWithType = useCallback((type) => {
    setSubsidyRows((prev) => [...prev, { name: type, amounts: createInitialSubsidyAmounts() }]);
  }, []);

  const handleRemoveSubsidy = useCallback((sri) => {
    setSubsidyRows((prev) => prev.filter((_, i) => i !== sri));
  }, []);

  const handleSubsidyNameChange = useCallback((sri, name) => {
    setSubsidyRows((prev) => prev.map((sr, i) => (i === sri ? { ...sr, name } : sr)));
  }, []);

  const handleSubsidyAmountChange = useCallback((sri, productCode, amount) => {
    setSubsidyRows((prev) =>
      prev.map((sr, i) =>
        i === sri ? { ...sr, amounts: { ...sr.amounts, [productCode]: amount } } : sr,
      ),
    );
  }, []);

  // ── Validation ──
  const hasValidProductRows = useMemo(() => {
    const hasPricingWithoutDepot = productRows.some(
      (row) =>
        !row.depot && (row.freight || row.pumpPrice || row.previousPumpPrice || row.stationUm),
    );
    if (hasPricingWithoutDepot) return false;
    const rowsWithDepot = productRows.filter((row) => row.depot);
    if (rowsWithDepot.length === 0) return false;
    return rowsWithDepot.every((row) => {
      return (
        row.freight &&
        row.pumpPrice &&
        (row.previousPumpPriceDisabled || row.previousPumpPrice) &&
        row.stationUm
      );
    });
  }, [productRows]);

  const isFormValid =
    isFormSubmittable &&
    !!stationDetails &&
    hasValidProductRows &&
    !conflictStatus &&
    !movementConflict &&
    !isCheckingConflict;

  const isDisabledAddSubsidy = useMemo(() => productRows.every((row) => !row.depot), [productRows]);

  // ── Save flow ──
  const handleSaveClick = useCallback(() => {
    setSaveAttempted(true);
    if (!isFormValid) return;
    confirmModal.show();
  }, [isFormValid, confirmModal]);

  const handleConfirmSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const effectiveAt = fields.effectiveDateTime.value;

      const payload = {
        stationId: stationDetails.stationId,
        effectiveAt,
        createdBy: user?.fullName || user?.name || user?.email,
        createdByEmail: user?.email,
        priceBuildupDetails: productRows
          .filter((row) => row.depot)
          .map((row) => {
            const lbam = parseNum(
              subsidyRows.find((s) => s.name === "LBAM")?.amounts[row.productCode] || 0,
            );
            const pbc = parseNum(
              subsidyRows.find((s) => s.name === "PBC")?.amounts[row.productCode] || 0,
            );
            const pam = parseNum(
              subsidyRows.find((s) => s.name === "PAM")?.amounts[row.productCode] || 0,
            );
            const totalSubsidies = subsidyRows.reduce(
              (sum, sr) => sum + parseNum(sr.amounts[row.productCode] || 0),
              0,
            );
            const wpp = computeWpp(row, subsidyRows);
            const wppAdjNum = parseNum(row.wppAdjustment);
            return {
              productCode: row.productCode,
              depotId: Number(row.depot),
              freight: parseNum(row.freight),
              pumpPrice: parseNum(row.pumpPrice),
              adjustment: parseNum(row.pumpPrice) - parseNum(row.previousPumpPrice),
              oldPumpPrice: parseNum(row.previousPumpPrice),
              oldFinalWpp: parseNum(row.previousFinalWpp),
              lbam,
              pbc,
              pam,
              totalSubsidies,
              stationUm: parseNum(row.stationUm),
              wpp,
              wppAdjustment: wppAdjNum,
              finalWpp: wpp + wppAdjNum,
            };
          }),
      };
      await submitCreate(payload);
      confirmModal.close();
      successModal.show();
    } catch (err) {
      if (err?.data?.errorCode === "PB1003") {
        const msg = err?.data?.message || "";
        const match = msg.toLowerCase().match(/\b(active|scheduled|expired)\b/);
        const status = match ? match[1].charAt(0).toUpperCase() + match[1].slice(1) : "Active";
        confirmModal.close();
        setConflictStatus(status);
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    stationDetails,
    fields.effectiveDateTime.value,
    user,
    productRows,
    subsidyRows,
    submitCreate,
    confirmModal,
    successModal,
  ]);

  const handleSuccessGotIt = useCallback(() => {
    successModal.close();
    history.push(Path.PriceBuildUpList);
  }, [successModal, history]);

  const showCellError = (row, value) => saveAttempted && !!row.depot && !value;

  // ── Subsidies computed values ──
  const canAddMoreSubsidies = subsidyRows.length < 3;
  const subsidiesRowSpan = subsidyRows.length + (canAddMoreSubsidies ? 1 : 0) + 1;

  const isAnyLoading = isLoadingAllDepots || isLoadingGetPriceBuildup || isLoadingGetPriceBuildups;
  const isShowStationDetails =
    stationResult &&
    fields.stationCode.value &&
    !fields.stationCode.error &&
    !stationCodeError &&
    !isAnyLoading;
  // ── Render ──
  return (
    <div className={styles.container} data-testid="pbu-add-page">
      <ConfirmModal
        {...confirmModal}
        title={locale.addNewPriceBuildUpQuestion}
        content={locale.addNewPriceBuildUpConfirmContent}
        primary={{
          text: locale.yesAddPriceBuildUp,
          onClick: handleConfirmSave,
          loading: isSaving,
        }}
        secondary={{ text: locale.cancel }}
      />
      <SuccessModal
        {...successModal}
        title={locale.success}
        content={locale.newPriceBuildUpSuccessfullyCreated}
        primary={{ text: "Got It", onClick: handleSuccessGotIt }}
      />

      <BackLink text={locale.priceBuildUp} path={Path.PriceBuildUpList} />

      <div className={styles.header}>
        <Title>{locale.addNewPriceBuildUp}</Title>
      </div>

      <Text className={styles.sectionTitle}>{locale.stationDetails}</Text>

      {/* ── Station Code ── */}
      <div className={styles.section}>
        <Field
          {...fields.stationCode}
          error={fields.stationCode.error || !!stationCodeError}
          message={fields.stationCode.message || stationCodeError}
          label={locale.stationCode}
          className={styles.fieldRow}
          labelPosition="left"
        >
          <TextField
            {...fields.stationCode}
            error={fields.stationCode.error || !!stationCodeError}
            value={stationCodeValue}
            placeholder="e.g. 5300"
            onChange={handleStationCodeChange}
            loading={loadingStation}
            data-testid="pbu-add-station-code"
          />
        </Field>
        {stationDetails && <StationDetails details={stationDetails} />}
      </div>

      {isAnyLoading && (
        <div className={`${styles.container} ${styles.fadeIn}`}>
          <div className={styles.section}>
            <Skeleton variant="text" width={180} height={22} style={{ marginBottom: 20 }} />
            <Skeleton
              variant="rect"
              width={500}
              height={40}
              style={{ borderRadius: 4, marginBottom: 16 }}
            />
            <Skeleton
              variant="rect"
              width={180}
              height={28}
              style={{ borderRadius: 14, marginBottom: 20 }}
            />
            <Skeleton variant="rect" width="100%" height={420} style={{ borderRadius: 8 }} />
          </div>
        </div>
      )}

      {/* ── Price Build-Up Details ── */}
      {isShowStationDetails && (
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>{locale.priceBuildUpDetails}</Text>

          <div className={styles.effectiveDateTimeRow}>
            <Field
              label="Effective Date and Time"
              labelPosition={"left"}
              className={styles.dateField}
              error={!!(conflictStatus || movementConflict)}
              message={
                isCheckingConflict ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <CircularProgress size={12} />
                    <Text>Checking for conflicts...</Text>
                  </span>
                ) : conflictStatus ? (
                  <Text className={styles.dateError}>
                    {"AEIOU".includes(conflictStatus?.[0]) ? "An" : "A"}{" "}
                    <strong>{conflictStatus}</strong> price build-up already exists for this station
                    at the selected effective time. Please choose a different effective time.
                  </Text>
                ) : movementConflict ? (
                  <Text className={styles.dateError}>
                    A <strong>Scheduled</strong> price movement will be implemented at the selected
                    effective date and time. Please choose a different effective date and time.
                  </Text>
                ) : null
              }
            >
              <DateTimePickerPopup
                name="effectiveDateTime"
                value={fields.effectiveDateTime.value}
                placeholder="Select date and time"
                onChange={handleEffectiveDateTimeChange}
                actions={{ clear: true, cancel: true, ok: true }}
                className={styles.effectiveDateTime}
                data-testid="pbu-add-effective-date"
                error={!!(conflictStatus || movementConflict)}
              />
            </Field>
          </div>

          {/* Price table */}
          <div className={styles.tableWrapper}>
            <table className={styles.priceTable}>
              <thead>
                <tr>
                  <th className={styles.groupHeader}></th>
                  <th className={styles.fieldHeader}></th>
                  {productRows.map((row) => (
                    <th
                      key={row.productCode}
                      className={styles.productHeader}
                      data-testid={`pbu-add-product-col-${row.productCode}`}
                    >
                      <span
                        className={styles.productBadge}
                        style={prettifyProductCodeStyle(row.productCode)}
                      />
                      {row.productName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* ── FREIGHT ── */}
                <tr>
                  <td rowSpan={2} className={styles.groupCell}>
                    {locale.freight}
                  </td>
                  <td className={styles.fieldCell}>{locale.depot}</td>
                  {productRows.map((row, i) => (
                    <td key={row.productCode}>
                      <DropdownPaginated
                        className={styles.depotSelect}
                        value={row.depot || ""}
                        label={row.depotLabel || ""}
                        request={depotRequest}
                        formatRequest={(res) =>
                          (res?.depots || []).map((d) => ({
                            label: d.depotCode,
                            value: String(d.depotId),
                          }))
                        }
                        total={allDepotsData.length}
                        placeholder="Select"
                        onChange={(_, opt) => handleDepotChange(i, opt)}
                      />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className={styles.fieldCell}>{locale.freight}</td>
                  {productRows.map((row, i) => (
                    <td key={row.productCode}>
                      <TextField
                        className={styles.numInput}
                        value={row.freight}
                        placeholder="0.0000"
                        disabled={!row.depot}
                        error={showCellError(row, row.freight)}
                        onChange={(_, { value }) => {
                          if (isNumericPeriod(value, 4) && parseNum(value) <= 999.9999)
                            updateRow(i, { freight: value });
                        }}
                        onBlur={(_, { value }) => {
                          if (value) {
                            const n = Math.min(parseNum(value), 999.9999);
                            updateRow(i, { freight: fmt4(n) });
                          }
                        }}
                      />
                    </td>
                  ))}
                </tr>

                {/* ── PUMP PRICE ── */}
                <tr>
                  <td rowSpan={3} className={styles.groupCell}>
                    {locale.pumpPrice}
                  </td>
                  <td className={styles.fieldCell}>{locale.pumpPrice}</td>
                  {productRows.map((row, i) => (
                    <td key={row.productCode}>
                      <TextField
                        className={styles.numInput}
                        value={row.pumpPrice}
                        placeholder="0.00"
                        disabled={!row.depot}
                        error={showCellError(row, row.pumpPrice)}
                        onChange={(_, { value }) => {
                          if (isNumericPeriod(value, 2) && parseNum(value) <= 999.99)
                            updateRow(i, { pumpPrice: value });
                        }}
                        onBlur={(_, { value }) => {
                          if (value) {
                            const n = Math.min(parseNum(value), 999.99);
                            updateRow(i, { pumpPrice: fmt2(n) });
                          }
                        }}
                      />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className={styles.fieldCell}>{locale.adjustment}</td>
                  {productRows.map((row) => {
                    const adj = parseNum(row.pumpPrice) - parseNum(row.previousPumpPrice);
                    return (
                      <td
                        key={row.productCode}
                        style={{ backgroundColor: "#f6f6f6", paddingLeft: "27px" }}
                      >
                        <Text
                          className={classNames(styles.computed, {
                            [styles.positive]: row.depot && adj > 0,
                            [styles.negative]: row.depot && adj < 0,
                          })}
                        >
                          {row.depot ? fmt2(adj) : "0.00"}
                        </Text>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className={styles.fieldCell}>{locale.previousPumpPrice}</td>
                  {productRows.map((row, i) => (
                    <td
                      key={row.productCode}
                      style={{
                        backgroundColor: row.previousPumpPriceDisabled ? "#f6f6f6" : undefined,
                      }}
                    >
                      {row.previousPumpPriceDisabled ? (
                        <Text className={styles.computed} style={{ paddingLeft: "13px" }}>
                          {row.previousPumpPrice || "0.00"}
                        </Text>
                      ) : (
                        <TextField
                          className={styles.numInput}
                          value={row.previousPumpPrice}
                          placeholder="0.00"
                          disabled={!row.depot}
                          error={showCellError(row, row.previousPumpPrice)}
                          onChange={(_, { value }) => {
                            if (isNumericPeriod(value, 2) && parseNum(value) <= 999.99)
                              updateRow(i, { previousPumpPrice: value });
                          }}
                          onBlur={(_, { value }) => {
                            if (value) {
                              const n = Math.min(parseNum(value), 999.99);
                              updateRow(i, { previousPumpPrice: fmt2(n) });
                            }
                          }}
                        />
                      )}
                    </td>
                  ))}
                </tr>
                {/* ── SUBSIDIES ── each subsidy type is its own row ── */}
                {subsidyRows.map((subsRow, sri) => {
                  const usedNames = subsidyRows.map((s) => s.name).filter(Boolean);
                  const optsForRow = SUBSIDY_OPTIONS.filter(
                    (opt) => !usedNames.includes(opt.value) || opt.value === subsRow.name,
                  );
                  return (
                    <tr key={`sub-${sri}`}>
                      {sri === 0 && (
                        <td rowSpan={subsidiesRowSpan} className={styles.groupCell}>
                          {locale.subsidies}
                        </td>
                      )}
                      <td className={styles.fieldCell}>
                        <div className={styles.subsidyFieldCell}>
                          <Autocomplete
                            value={subsRow.name}
                            options={optsForRow}
                            className={styles.subsidyTypeSelect}
                            placeholder="Select"
                            onChange={(_, { value }) => handleSubsidyNameChange(sri, value)}
                          />
                          <button
                            type="button"
                            className={styles.removeSubsidy}
                            onClick={() => handleRemoveSubsidy(sri)}
                          >
                            ×
                          </button>
                        </div>
                      </td>
                      {productRows.map((row, pi) => (
                        <td key={row.productCode}>
                          <TextField
                            className={styles.numInput}
                            value={subsRow.amounts[row.productCode] || ""}
                            placeholder="0.00"
                            disabled={!row.depot || !subsRow.name}
                            onChange={(_, { value }) => {
                              if (isNumericPeriod(value, 2) && parseNum(value) <= 999.99)
                                handleSubsidyAmountChange(sri, row.productCode, value);
                            }}
                            onBlur={(_, { value }) => {
                              if (value) {
                                const n = Math.min(parseNum(value), 999.99);
                                handleSubsidyAmountChange(sri, row.productCode, fmt2(n));
                              }
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}

                {/* Empty subsidy dropdown row — visible while types remain */}
                {canAddMoreSubsidies && (
                  <tr>
                    {subsidyRows.length === 0 && (
                      <td rowSpan={subsidiesRowSpan} className={styles.groupCell}>
                        {locale.subsidies}
                      </td>
                    )}
                    <td className={styles.fieldCell}>
                      <Autocomplete
                        key={subsidyRows.length}
                        value={null}
                        options={SUBSIDY_OPTIONS.filter(
                          (opt) => !subsidyRows.some((sr) => sr.name === opt.value),
                        )}
                        className={styles.subsidyTypeSelect}
                        placeholder="Add Subsidy"
                        onChange={(_, { value }) => handleAddSubsidyWithType(value)}
                        disabled={isDisabledAddSubsidy}
                      />
                    </td>
                    {productRows.map((row) => (
                      <td key={row.productCode}>
                        <TextField
                          className={styles.numInput}
                          value=""
                          placeholder="0.00"
                          disabled
                        />
                      </td>
                    ))}
                  </tr>
                )}

                {/* Subsidies TOTAL row */}
                <tr>
                  {!canAddMoreSubsidies && subsidyRows.length === 0 && (
                    <td className={styles.groupCell}>{locale.subsidies}</td>
                  )}
                  <td className={styles.fieldCell}>{locale.subsidiesTotal}</td>
                  {productRows.map((row) => {
                    const total = subsidyRows.reduce(
                      (sum, sr) => sum + parseNum(sr.amounts[row.productCode] || 0),
                      0,
                    );
                    return (
                      <td
                        key={row.productCode}
                        style={{ backgroundColor: "#f6f6f6", paddingLeft: "27px" }}
                      >
                        <Text className={classNames(styles.computed, styles.totalCell)}>
                          {row.depot ? fmt2(total) : "0.00"}
                        </Text>
                      </td>
                    );
                  })}
                </tr>

                {/* ── MARGINS ── */}
                <tr>
                  <td className={styles.groupCell}>{locale.margins}</td>
                  <td className={styles.fieldCell}>{locale.stationUm}</td>
                  {productRows.map((row, i) => (
                    <td key={row.productCode}>
                      <TextField
                        className={styles.numInput}
                        value={row.stationUm}
                        placeholder="0.00"
                        disabled={!row.depot}
                        error={showCellError(row, row.stationUm)}
                        onChange={(_, { value }) => {
                          if (isNumericPeriod(value, 2) && parseNum(value) <= 99.99)
                            updateRow(i, { stationUm: value });
                        }}
                        onBlur={(_, { value }) => {
                          if (value) {
                            const n = Math.min(parseNum(value), 99.99);
                            updateRow(i, { stationUm: fmt2(n) });
                          }
                        }}
                      />
                    </td>
                  ))}
                </tr>

                {/* ── WHOLESALE POSTED PRICE ── */}
                <tr>
                  <td rowSpan={4} className={styles.groupCell}>
                    {locale.wholesalePostedPrice}
                  </td>
                  <td className={styles.fieldCell}>{locale.netWpp}</td>
                  {productRows.map((row) => (
                    <td
                      key={row.productCode}
                      style={{ backgroundColor: "#f6f6f6", paddingLeft: "27px" }}
                    >
                      <Text className={styles.computed}>
                        {row.depot ? fmt4(computeWpp(row, subsidyRows)) : "0.0000"}
                      </Text>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className={styles.fieldCell}>{locale.wppAdjustment}</td>
                  {productRows.map((row, i) => (
                    <td key={row.productCode}>
                      <TextField
                        className={styles.numInput}
                        value={row.wppAdjustment}
                        placeholder="0.0000"
                        disabled={!row.depot || row.wppAdjustmentDisabled}
                        onChange={(_, { value }) => {
                          const n = parseFloat(value) || 0;
                          if (isNumericPeriodMinus(value, 4) && n >= -999.9999 && n <= 999.9999)
                            updateRow(i, { wppAdjustment: value });
                        }}
                        onBlur={(_, { value }) => {
                          if (value) {
                            const n = Math.min(
                              Math.max(parseFloat(value) || 0, -999.9999),
                              999.9999,
                            );
                            updateRow(i, { wppAdjustment: fmt4(n) });
                          }
                        }}
                      />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className={styles.fieldCell}>{locale.orderingWpp}</td>
                  {productRows.map((row) => {
                    const finalWpp = computeWpp(row, subsidyRows) + parseNum(row.wppAdjustment);
                    return (
                      <td
                        key={row.productCode}
                        style={{ backgroundColor: "#f6f6f6", paddingLeft: "27px" }}
                      >
                        <Text className={styles.computed}>
                          {row.depot ? fmt4(finalWpp) : "0.0000"}
                        </Text>
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className={styles.fieldCell}>{locale.previousFinalWpp}</td>
                  {productRows.map((row, i) => (
                    <td
                      key={row.productCode}
                      style={{
                        backgroundColor: row.previousFinalWppDisabled ? "#f6f6f6" : undefined,
                      }}
                    >
                      {row.previousFinalWppDisabled ? (
                        <Text className={styles.computed} style={{ paddingLeft: "13px" }}>
                          {row.previousFinalWpp || "0.0000"}
                        </Text>
                      ) : (
                        <TextField
                          className={styles.numInput}
                          value={row.previousFinalWpp}
                          placeholder="0.0000"
                          disabled={!row.depot}
                          onChange={(_, { value }) => {
                            if (isNumericPeriod(value, 4) && parseNum(value) <= 9999.9999)
                              updateRow(i, { previousFinalWpp: value });
                          }}
                          onBlur={(_, { value }) => {
                            if (value) {
                              const n = Math.min(parseNum(value), 9999.9999);
                              updateRow(i, { previousFinalWpp: fmt4(n) });
                            }
                          }}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {saveAttempted && !hasValidProductRows && (
            <Text className={styles.tableError}>
              {productRows.every((r) => !r.depot)
                ? "Please select a depot for at least one product."
                : productRows.some(
                      (r) =>
                        !r.depot &&
                        (r.freight || r.pumpPrice || r.previousPumpPrice || r.stationUm),
                    )
                  ? "Please select a depot for all products with pricing data."
                  : "Please fill in all required fields for selected products."}
            </Text>
          )}
        </div>
      )}

      <div className={styles.saveContainer}>
        <Button
          primary
          className={!isFormValid ? styles.saveBtnInactive : undefined}
          onClick={handleSaveClick}
          data-testid="pbu-add-save-btn"
        >
          {locale.save}
        </Button>
      </div>
    </div>
  );
};

export default PriceBuildUpAddModule;
